import { Injectable, OnDestroy } from '@angular/core';
import { forkJoin, Subject, Subscription, of, interval, Observable } from 'rxjs';
import { catchError, takeUntil, switchMap, map } from 'rxjs/operators';
import { KeycloakService } from 'keycloak-angular';
import { SelfSpaceApiService } from './self-space-api.service';
import { SelfSpaceStateService } from './self-space-state.service';
import { SelfSpaceAdapterService } from './self-space-adapter.service';
import { SelfSpaceWebSocketService } from './self-space-websocket.service';
import { HitoProyectoService } from '../../../services/hito-proyecto.service';
import { ProyectService } from '../../../services/proyect.service';
import { KanbanApiService } from '../../kanban/services/kanban-api.service';
import { RegistroTiempoGroupResponse } from '../models/registro-tiempo.model';
import { WeekRange, ModalState, ModalFlow } from '../models/ui.model';
import { AuthStateService } from '../../../core/services/auth-state.service';
import { NotificationService } from '../../../core/services/notification.service';
// swal retenido solo para diálogo con input select (promptOverloadProject)
import swal from 'sweetalert2';

@Injectable({ providedIn: 'root' })
export class SelfSpaceFacadeService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private pollingSub: Subscription | null = null;
  private wsInitialized = false;
  private readonly OVERLOAD_STORAGE_KEY = 'selfspace_last_overload_notification_date';

  constructor(
    private readonly apiService: SelfSpaceApiService,
    private readonly stateService: SelfSpaceStateService,
    private readonly adapterService: SelfSpaceAdapterService,
    private readonly wsService: SelfSpaceWebSocketService,
    private readonly hitoProyectoService: HitoProyectoService,
    private readonly proyectService: ProyectService,
    private readonly kanbanApiService: KanbanApiService,
    private readonly keycloakService: KeycloakService,
    private readonly authState: AuthStateService,
    private readonly notification: NotificationService
  ) {}

  ngOnDestroy(): void {
    this.stopPolling();
    this.wsService.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // === Initial Load ===

  loadDashboard(): void {
    const email = this.getUserEmail();
    const weekRange = this.stateService.currentWeek;

    // Inicializar flag de sobrecarga notificada en base al límite diario
    const todayKey = this.getTodayKey();
    const lastNotified = localStorage.getItem(this.OVERLOAD_STORAGE_KEY);
    this.stateService.setSobrecargaNotificada(lastNotified === todayKey);

    this.stateService.setLoading(true);

    forkJoin([
      this.apiService.getDashboard(email, weekRange).pipe(
        catchError(err => {
          console.error('Error loading dashboard:', err);
          return of(null);
        })
      ),
      this.hitoProyectoService.findHitosProyectUserNameWeek(1, 1000, email.toUpperCase(), weekRange.startDate, weekRange.endDate).pipe(
        catchError(err => {
          console.error('Error loading tareas:', err);
          return of(null);
        })
      ),
      this.kanbanApiService.getMyProjects().pipe(
        catchError(err => {
          console.error('Error loading kanban projects for clienteMap:', err);
          return of([] as any[]);
        })
      )
    ])
    .pipe(takeUntil(this.destroy$))
    .subscribe(([dashboard, tareas, kanbanProjects]) => {
      if (dashboard) {
        this.stateService.setRacha(dashboard.racha);
        this.stateService.setCargaTrabajo(dashboard.cargaTrabajo);
        this.stateService.setResumen(dashboard.resumenDetallado);
        this.stateService.setEscuadron(dashboard.escuadron);
        this.stateService.setInsignias(dashboard.insignias);
        this.stateService.setNotificacionesPendientesCount(dashboard.notificacionesPendientes);
      }

      const clienteMap = new Map<number, string>();
      if (kanbanProjects) {
        for (const p of kanbanProjects) {
          if (p.proyectoId && p.clienteNombre) {
            clienteMap.set(p.proyectoId, p.clienteNombre);
          }
        }
      }

      if (tareas) {
        const adaptedTareas = this.adapterService.adaptTareas(
          tareas.content || tareas || [],
          clienteMap
        );
        this.stateService.setTareas(adaptedTareas);
      }

      this.stateService.setLoading(false);
    });
  }

  // === Week Navigation ===

  navigateWeek(direction: 'prev' | 'next'): void {
    const currentWeek = this.stateService.currentWeek;
    const newWeek = this.adapterService.navigateWeek(currentWeek, direction);

    this.stateService.setSelectedWeek(newWeek);
    this.stateService.resetForWeekChange();

    const currentWeekRange = this.adapterService.getWeekRange(new Date());
    const isHistorical = newWeek.startDate !== currentWeekRange.startDate;
    this.stateService.setIsHistorical(isHistorical);

    this.loadDashboard();
  }

  goToCurrentWeek(): void {
    const currentWeek = this.adapterService.getWeekRange(new Date());
    this.stateService.setSelectedWeek(currentWeek);
    this.stateService.setIsHistorical(false);
    this.stateService.resetForWeekChange();
    this.loadDashboard();
  }

  // === Modal Management ===

  openModal(flow: ModalFlow, options?: Partial<ModalState>): void {
    this.stateService.setModalState({
      open: true,
      flow,
      ...options
    });
  }

  closeModal(): void {
    this.stateService.setModalState({ open: false });
  }

  // === Post-Registration Refresh ===

  refreshAfterRegistration(): void {
    const email = this.getUserEmail();
    const weekRange = this.stateService.currentWeek;

    forkJoin([
      this.apiService.getRacha(email, weekRange, true).pipe(catchError(() => of(null))),
      this.apiService.getCargaTrabajo(email, weekRange, true).pipe(catchError(() => of(null))),
      this.apiService.getResumenDetallado(email, weekRange, true).pipe(catchError(() => of(null))),
      this.hitoProyectoService.findHitosProyectUserNameWeek(1, 1000, email.toUpperCase(), weekRange.startDate, weekRange.endDate, true).pipe(catchError(() => of(null))),
      this.apiService.getRegistrosGroupedByEmail(email, weekRange.startDate, weekRange.endDate, true).pipe(catchError(() => of(null))),
      this.kanbanApiService.getMyProjects().pipe(catchError(() => of([] as any[]))),
      this.apiService.getInsignias(email).pipe(catchError(() => of(null)))
    ])
    .pipe(takeUntil(this.destroy$))
    .subscribe(([racha, carga, resumen, tareas, historial, kanbanProjects, insignias]) => {
      if (racha) this.stateService.setRacha(racha);
      if (carga) {
        this.stateService.setCargaTrabajo(carga);
        if (carga.sobrecarga && !this.stateService.isSobrecargaNotificada) {
          this.stateService.setSobrecargaNotificada(true);
        }
      }
      if (resumen) this.stateService.setResumen(resumen);

      const clienteMap = new Map<number, string>();
      if (kanbanProjects) {
        for (const p of kanbanProjects) {
          if (p.proyectoId && p.clienteNombre) {
            clienteMap.set(p.proyectoId, p.clienteNombre);
          }
        }
      }

      if (tareas) {
        this.stateService.setTareas(
          this.adapterService.adaptTareas(tareas.content || tareas || [], clienteMap)
        );
      }
      if (historial) {
        this.resolveProjectNames(historial)
          .pipe(takeUntil(this.destroy$))
          .subscribe((projectMap: Map<number, string>) => {
            this.stateService.setHistorial(
              this.adapterService.adaptHistorial(historial, projectMap)
            );
          });
      }
      if (insignias) {
        this.stateService.setInsignias(insignias);
      }
    });
  }

  // === Historial ===

  loadHistorial(): void {
    const email = this.getUserEmail();
    const weekRange = this.stateService.currentWeek;

    this.apiService.getRegistrosGroupedByEmail(email, weekRange.startDate, weekRange.endDate, true)
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => { console.error('Error loading historial:', err); return of([] as RegistroTiempoGroupResponse[]); }),
        switchMap(data => this.resolveProjectNames(data).pipe(
          map(projectMap => ({ data, projectMap }))
        ))
      )
      .subscribe(({ data, projectMap }) => {
        this.stateService.setHistorial(this.adapterService.adaptHistorial(data, projectMap));
      });
  }

  // === Notificaciones ===

  loadNotificaciones(): void {
    const email = this.getUserEmail();
    this.apiService.getNotificaciones(email)
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => { console.error('Error loading notificaciones:', err); return of([]); })
      )
      .subscribe(notificaciones => {
        this.stateService.setNotificaciones(notificaciones);
      });
  }

  markNotificacionAsRead(id: number): void {
    const email = this.getUserEmail();
    this.apiService.marcarNotificacionesLeidas({ email, ids: [id] })
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => { console.error('Error marking notification:', err); return of(null); })
      )
      .subscribe(() => {
        const current = this.stateService.currentNotificaciones.map(n =>
          n.id === id ? { ...n, leida: true } : n
        );
        this.stateService.setNotificaciones(current);
        this.stateService.setNotificacionesPendientesCount(current.filter(n => !n.leida).length);
      });
  }

  markAllNotificacionesAsRead(): void {
    const email = this.getUserEmail();
    this.apiService.marcarNotificacionesLeidas({ email, ids: [] })
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => { console.error('Error marking all notifications:', err); return of(null); })
      )
      .subscribe(() => {
        const current = this.stateService.currentNotificaciones.map(n => ({ ...n, leida: true }));
        this.stateService.setNotificaciones(current);
        this.stateService.setNotificacionesPendientesCount(0);
      });
  }

  // === Squad ===

  animarEquipo(mensaje: string): void {
    const email = this.getUserEmail();
    const weekRange = this.stateService.currentWeek;
    this.apiService.animarEscuadron({ email, mensaje }, weekRange)
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => { console.error('Error animando equipo:', err); return of(void 0); })
      )
      .subscribe();
  }

  async notificarSobrecarga(): Promise<void> {
    const email = this.getUserEmail();
    if (!email) {
      return;
    }

    // Evitar notificaciones duplicadas el mismo día
    const todayKey = this.getTodayKey();
    const lastNotified = localStorage.getItem(this.OVERLOAD_STORAGE_KEY);      
    if (lastNotified === todayKey) {
      this.notification.alert('No puedes notificar más hoy', 'Solo puedes notificar una vez por día.', 'error');
      return;
    }

    const weekRange = this.stateService.currentWeek;
    const currentWeekRange = this.adapterService.getWeekRange(new Date());
    const isHistorical = weekRange.startDate !== currentWeekRange.startDate;
    if (isHistorical) {
      return;
    }

    const selection = await this.selectProyectoForSobrecarga();
    if (!selection) {
      // Usuario canceló el diálogo de selección
      return;
    }

    const { proyectoId, detalleProyecto } = selection;
    let mensaje = `Sobrecarga de trabajo en la semana del ${weekRange.startDate} al ${weekRange.endDate}`;
    if (detalleProyecto) {
      mensaje += detalleProyecto;
    }

    this.apiService
      .notificarBloqueo(email, { hitoProyectoId: null, proyectoId, mensaje })
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => {
          console.error('Error notificando sobrecarga:', err);
          return of(void 0);
        })
      )
      .subscribe(() => {
        this.stateService.setSobrecargaNotificada(true);
        localStorage.setItem(this.OVERLOAD_STORAGE_KEY, todayKey);
      });
  }

  // === Tab Management ===

  setActiveTab(tab: 'tareas' | 'resumen' | 'historial'): void {
    this.stateService.setActiveTab(tab);
    if (tab === 'historial') {
      this.loadHistorial();
    }
  }

  // === WebSocket Integration ===

  initWebSocket(): void {
    if (this.wsInitialized) return;
    this.wsInitialized = true;

    // Connect WebSocket
    this.wsService.connect();

    // Graceful degradation: poll when disconnected
    this.wsService.isConnected$
      .pipe(takeUntil(this.destroy$))
      .subscribe(connected => {
        if (connected) {
          this.stopPolling();
          this.subscribeToTopics();
        } else {
          this.startPolling();
        }
      });
  }

  private subscribeToTopics(): void {
    const email = this.getUserEmail();
    const equipoId = this.stateService.currentEscuadronEquipoId;

    // Notifications
    this.wsService.watchNotificaciones(email)
      .pipe(takeUntil(this.destroy$))
      .subscribe(notificacion => {
        this.stateService.addNotificacion(notificacion);
        this.stateService.setNotificacionesPendientesCount(
          this.stateService.currentNotificaciones.filter(n => !n.leida).length
        );
      });

    // Squad updates
    if (equipoId) {
      this.wsService.watchEscuadron(equipoId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(escuadronUpdate => {
          this.stateService.setEscuadron(escuadronUpdate);
        });
    }

    // Badge updates — refresh via HTTP for consistency
    this.wsService.watchInsignias(email)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.apiService.getInsignias(email).pipe(catchError(() => of([]))))
      )
      .subscribe(insignias => this.stateService.setInsignias(insignias));
  }

  private startPolling(): void {
    if (this.pollingSub) return;

    this.pollingSub = interval(60000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {
          const email = this.getUserEmail();
          return this.apiService.getNotificaciones(email).pipe(catchError(() => of([])));
        })
      )
      .subscribe(notificaciones => {
        this.stateService.setNotificaciones(notificaciones);
        this.stateService.setNotificacionesPendientesCount(
          notificaciones.filter(n => !n.leida).length
        );
      });
  }

  private stopPolling(): void {
    if (this.pollingSub) {
      this.pollingSub.unsubscribe();
      this.pollingSub = null;
    }
  }

  // === Helpers ===

  private resolveProjectNames(groups: RegistroTiempoGroupResponse[]): Observable<Map<number, string>> {
    const missingIds = new Set<number>();
    for (const group of groups) {
      for (const r of (group.registerTimes || [])) {
        if (r.proyectoId && !r.proyecto) {
          missingIds.add(r.proyectoId);
        }
      }
    }

    if (missingIds.size === 0) {
      return of(new Map<number, string>());
    }

    const calls = Array.from(missingIds).map(id =>
      this.proyectService.getProyectById(id).pipe(
        map((p: any) => ({ id, nombre: p?.nombre || p?.name || '' })),
        catchError(() => of({ id, nombre: '' }))
      )
    );

    return forkJoin(calls).pipe(
      map(results => {
        const projectMap = new Map<number, string>();
        for (const r of results) {
          if (r.nombre) {
            projectMap.set(r.id, r.nombre);
          }
        }
        return projectMap;
      })
    );
  }

  private getUserEmail(): string {
    return this.authState.userDataAuth?.correo || '';
  }

  private getTodayKey(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Permite seleccionar a qué proyecto asociar la sobrecarga.
    * - Si no hay resumen o proyectos con horas, retorna selección "general" (sin proyectoId).
    * - Si hay proyectos con horas, abre un select usando SweetAlert2 para que el usuario elija
    *   (incluyendo opción "general"). Si solo hay uno, se preselecciona en el combo.
   */
  private async selectProyectoForSobrecarga(): Promise<{ proyectoId: number | null; detalleProyecto: string } | null> {
    const resumen = this.stateService.currentResumen;
    if (!resumen || !resumen.proyectos || resumen.proyectos.length === 0) {
      return { proyectoId: null, detalleProyecto: '' };
    }

    const proyectosConHoras = resumen.proyectos.filter(p => p.horas > 0);
    if (proyectosConHoras.length === 0) {
      return { proyectoId: null, detalleProyecto: '' };
    }

    // Caso: uno o varios proyectos -> pedir al usuario que elija (con opción general)
    const inputOptions: Record<string, string> = {};
    for (const p of proyectosConHoras) {
      inputOptions[p.proyectoId.toString()] = `${p.proyectoNombre} – ${p.horas}h esta semana`;
    }
    inputOptions['general'] = 'Varios proyectos / general';

    const preselectedId = proyectosConHoras.length === 1
      ? proyectosConHoras[0].proyectoId.toString()
      : undefined;

    const result = await swal.fire<string>({
      title: '¿En qué proyecto te estás sobrecargando?',
      text: 'Solo se notificará al gestor del proyecto que elijas (además de a ti).',
      input: 'select',
      inputOptions,
      inputPlaceholder: 'Selecciona un proyecto o "general"',
      inputValue: preselectedId,
      showCancelButton: true,
      confirmButtonText: 'Notificar sobrecarga',
      cancelButtonText: 'Cancelar',
      icon: 'warning'
    });

    if (!result.isConfirmed || !result.value) {
      return null;
    }

    const value = result.value as string;
    if (value === 'general') {
      return { proyectoId: null, detalleProyecto: '' };
    }

    const selectedId = Number(value);
    if (Number.isNaN(selectedId)) {
      return { proyectoId: null, detalleProyecto: '' };
    }

    const selected = proyectosConHoras.find(p => p.proyectoId === selectedId) || null;
    const detalleProyecto = selected ? ` en el proyecto ${selected.proyectoNombre}` : '';
    return { proyectoId: selectedId, detalleProyecto };
  }
}
